import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { Invoice } from '../../models/invoice/invoice.model';
import { InternalMember } from '../../models/member/internal-member.model';
import { RoomBooking } from '../../models/room-booking/room-booking.model';
import { map } from 'rxjs/operators';
import { ExchangeApiService } from '../exchange-api.service';
import { Room } from '../../models/room-booking/room.model';
import { Meridian } from '../../models/meridian';
import { BuildingOpeningHour } from '../../models/room-booking/building-opening-hour.model';

export interface TimePeriod {
  startPeriodAm: Date;
  endPeriodAm: Date;
  startPeriodPm: Date;
  endPeriodPm: Date;
}

@Injectable()
export class RoomBookingService {

  path = 'room_bookings';

  private openingDaysSubject: ReplaySubject<any[]> = new ReplaySubject<any[]>(0);

  constructor(
    private api: ExchangeApiService,
  ) {
  }

  public getOpeningDaysSelected(): Observable<any[]> {
    return this.openingDaysSubject.asObservable();
  }

  public setOpeningDaysSelected(openingDays): void {
    this.openingDaysSubject.next(openingDays);
  }


  /**
   * Returns room booking based on its id
   *
   * @param id Room booking id
   */
  get(id: number): Observable<RoomBooking> {
    return this.api
      .get(`${this.path}/${id}`)
      .pipe(
        map(data => new RoomBooking(data)),
      );
  }

  getInvoice(id: number): Observable<Invoice> {
    return this.api
      .get(`${this.path}/${id}/invoice`)
      .pipe(
        map(data => new Invoice(data)),
      );
  }

  /**
   * Sends a room booking to Exchange BFF in order to create it
   *
   * @param roomBooking Room booking to create
   */
  post(roomBooking): Observable<RoomBooking> {
    return this.api.post('room_bookings', roomBooking)
      .pipe(
        map(data => new RoomBooking(data)),
      );
  }

  /**
   * Cancels a room booking based on its id
   *
   * @param id Room booking id
   */
  cancel(id: number): Observable<any> {
    return this.api
      .delete(`${this.path}/${id}`)
      .pipe(
        map(data => data),
      );
  }

  getMyBookings(): Observable<RoomBooking[]> {
    return this.api.get('internal_members/me/room_bookings')
      .pipe(
        map(bookings => bookings.map(booking => new RoomBooking(booking))),
      );
  }

  edit(roomBooking: RoomBooking): Observable<RoomBooking> {
    if (typeof roomBooking.owner === 'string') {
      roomBooking.owner = new InternalMember({id: roomBooking.owner.split('/').pop()});
    }

    if (typeof roomBooking.room === 'string') {
      roomBooking.room = new Room({id: (roomBooking.room as string).split('/').pop()});
    }

    return this.api.put(`room_bookings/${roomBooking.id}`, roomBooking)
      .pipe(
        map(data => new RoomBooking(data)),
      );
  }

  public getDateByOpeningHours(date: Date, openingDays: any[], isStartField: boolean = false): Date {
    const timePeriod = this.getDayHoursPeriod(date, openingDays);

    if (date < timePeriod.startPeriodAm) {
      return timePeriod.startPeriodAm;
    } else if (date > timePeriod.endPeriodPm) {
      return timePeriod.endPeriodPm;
    } else if (date > timePeriod.endPeriodAm && date < timePeriod.startPeriodPm) {
      return isStartField ? timePeriod.endPeriodAm : timePeriod.startPeriodPm;
    } else {
      return date;
    }
  }

  public getDayHoursPeriod(date: Date, openingDays: any[]): TimePeriod {
    let dayNumber = date.getDay();
    dayNumber = dayNumber === 0 ? 7 : dayNumber;
    const openingHours = openingDays.find(openingDay => openingDay.day === dayNumber).openingHours;

    const [startPeriodAm, endPeriodAm] = this.getPeriod(date, openingHours as BuildingOpeningHour[], Meridian.am);
    const [startPeriodPm, endPeriodPm] = this.getPeriod(date, openingHours as BuildingOpeningHour[], Meridian.pm);

    return {startPeriodAm, endPeriodAm, startPeriodPm, endPeriodPm};
  }

  private getPeriod(date: Date, openingHours: any[], meridian): Date[] {
    const midDay = openingHours.find(openingHour => openingHour.period === meridian);
    const timeStart = midDay.periodStart.split('+')[0];
    const timeEnd = midDay.periodEnd.split('+')[0];
    const startPeriod = new Date(date.getTime());
    startPeriod.setHours(new Date(timeStart).getHours());
    startPeriod.setMinutes(new Date(timeStart).getMinutes());

    const endPeriod = new Date(date.getTime());
    endPeriod.setHours(new Date(timeEnd).getHours());
    endPeriod.setMinutes(new Date(timeEnd).getMinutes());

    return [startPeriod, endPeriod];
  }
}
